import { QuoteTemplate } from '@/types/quote';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Copy, Trash2, FileText, Star, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TemplateCardProps {
  template: QuoteTemplate;
  onEdit: (template: QuoteTemplate) => void;
  onDuplicate: (template: QuoteTemplate) => void;
  onDelete: (template: QuoteTemplate) => void;
}

export const TemplateCard = ({ template, onEdit, onDuplicate, onDelete }: TemplateCardProps) => {
  const pageCount = template.page_count || template.pdf_page_count || 1;
  const hasPdfTemplate = !!template.pdf_template_url;
  
  // Count features
  const features: string[] = [];
  if (template.default_project_goal) features.push('Projectdoel');
  if (template.default_work_description) features.push('Werkomschrijving');
  if (template.default_advice_conclusion) features.push('Advies');
  if (template.default_disclaimer) features.push('Disclaimer');
  if (template.default_payment_conditions?.length) features.push(`${template.default_payment_conditions.length} betalingsmijlpalen`);
  if (template.terms_document_url) features.push('Algemene voorwaarden PDF');
  if (template.template_terms) features.push('Voorwaarden tekst');
  
  return (
    <Card className={cn(
      "group relative transition-all hover:shadow-md",
      template.is_default && "ring-2 ring-primary/30"
    )}>
      <CardContent className="p-0">
        {/* Preview Section */}
        <div className="relative h-40 bg-muted/50 rounded-t-lg overflow-hidden flex items-center justify-center">
          {hasPdfTemplate ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/80 to-muted">
              <FileText className="h-12 w-12 text-muted-foreground/50" />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-background to-muted">
              <div 
                className="w-20 h-28 rounded border-2 border-dashed flex items-center justify-center"
                style={{ borderColor: template.brand_primary || 'hsl(var(--muted-foreground))' }}
              >
                <span className="text-[8px] text-muted-foreground">A4</span>
              </div>
            </div>
          )}
          
          {/* Page dots */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1">
            {Array.from({ length: Math.min(pageCount, 5) }, (_, i) => (
              <div 
                key={i}
                className={cn(
                  "w-2 h-2 rounded-full",
                  i === 0 ? "bg-primary" : "bg-muted-foreground/40"
                )}
              />
            ))}
            {pageCount > 5 && (
              <span className="text-[10px] text-muted-foreground ml-1">+{pageCount - 5}</span>
            )}
          </div>
          
          {/* Default badge */}
          {template.is_default && (
            <div className="absolute top-2 right-2">
              <Badge variant="default" className="gap-1">
                <Star className="h-3 w-3" />
                Standaard
              </Badge>
            </div>
          )}
          
          {/* Brand colors */}
          {(template.brand_primary || template.brand_secondary) && (
            <div className="absolute top-2 left-2 flex gap-1">
              {template.brand_primary && (
                <div 
                  className="w-4 h-4 rounded-full border border-background shadow-sm"
                  style={{ backgroundColor: template.brand_primary }}
                />
              )}
              {template.brand_secondary && (
                <div 
                  className="w-4 h-4 rounded-full border border-background shadow-sm"
                  style={{ backgroundColor: template.brand_secondary }}
                />
              )}
            </div>
          )}
        </div>
        
        {/* Info Section */}
        <div className="p-4 space-y-3">
          <div>
            <h3 className="font-semibold text-sm line-clamp-1">{template.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {pageCount} pagina{pageCount !== 1 ? "'s" : ""} 
              {hasPdfTemplate && ' • PDF template'}
            </p>
          </div>
          
          {/* Features */}
          {features.length > 0 && (
            <div className="space-y-1">
              {features.slice(0, 3).map((feature, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3 w-3 text-primary/70" />
                  <span className="line-clamp-1">{feature}</span>
                </div>
              ))}
              {features.length > 3 && (
                <p className="text-xs text-muted-foreground pl-4.5">
                  +{features.length - 3} meer
                </p>
              )}
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(template)}
              className="flex-1"
            >
              <Edit className="h-3.5 w-3.5 mr-1.5" />
              Bewerken
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onDuplicate(template)}
              title="Dupliceer template"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onDelete(template)}
              title="Verwijderen"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
